const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Error messages by HTTP status (no body parsing needed for errors)
const ERROR_MESSAGES = {
  400: 'Richiesta non valida.',
  401: 'Email o password non validi.',
  403: 'Non hai i permessi per eseguire questa operazione.',
  404: 'Risorsa non trovata.',
  409: 'Conflitto: la risorsa esiste gi\u00e0.',
  422: 'Dati non validi.',
  429: 'Troppe richieste. Riprova tra qualche secondo.',
  500: 'Errore interno del server.',
};

// Generic fetch wrapper with auth
async function apiFetch(endpoint, options = {}) {
  let response;
  try {
    response = await fetch(`${API}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
  } catch (err) {
    throw new Error('Impossibile contattare il server. Verifica la connessione.');
  }

  // Handle errors by status code (no body reading required)
  if (!response.ok) {
    throw new Error(ERROR_MESSAGES[response.status] || `Errore del server (${response.status})`);
  }

  // For successful responses, parse JSON body
  // Use a separate try/catch to handle empty responses (e.g. 204, logout)
  try {
    const data = await response.json();
    return data;
  } catch (e) {
    return null;
  }
}

// Auth
export const auth = {
  me: () => apiFetch('/auth/me'),
  login: (email, password) => apiFetch('/auth/login', { 
    method: 'POST', 
    body: JSON.stringify({ email, password }) 
  }),
  register: (email, password, name) => apiFetch('/auth/register', { 
    method: 'POST', 
    body: JSON.stringify({ email, password, name }) 
  }),
  logout: () => apiFetch('/auth/logout', { method: 'POST' }),
  changePassword: (currentPassword, newPassword) => apiFetch('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
  }),
  createUser: (data) => apiFetch('/auth/create-user', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  deleteUser: (userId) => apiFetch(`/auth/users/${userId}`, { method: 'DELETE' }),
};

// Persone
export const persone = {
  list: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return apiFetch(`/persone?${searchParams}`);
  },
  get: (id) => apiFetch(`/persone/${id}`),
  getByQr: (qrCode) => apiFetch(`/persone/qr/${qrCode}`),
  create: (data) => apiFetch('/persone', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiFetch(`/persone/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiFetch(`/persone/${id}`, { method: 'DELETE' }),
  updateBadge: (id, stato) => apiFetch(`/persone/${id}/badge?stato=${stato}`, { method: 'PATCH' }),
};

// Organizzazioni
export const organizzazioni = {
  list: () => apiFetch('/organizzazioni'),
  create: (data) => apiFetch('/organizzazioni', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiFetch(`/organizzazioni/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiFetch(`/organizzazioni/${id}`, { method: 'DELETE' }),
};

// Accessi
export const accessi = {
  list: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return apiFetch(`/accessi?${searchParams}`);
  },
  create: (data) => apiFetch('/accessi', { method: 'POST', body: JSON.stringify(data) }),
};

// Eventi Badge
export const eventiBadge = {
  list: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return apiFetch(`/eventi-badge?${searchParams}`);
  },
  create: (data) => apiFetch('/eventi-badge', { method: 'POST', body: JSON.stringify(data) }),
  scan: (qrCode, tipoEvento, dettagli = {}) => 
    apiFetch(`/eventi-badge/scan?qr_code=${qrCode}&tipo_evento=${tipoEvento}`, { 
      method: 'POST', 
      body: JSON.stringify(dettagli) 
    }),
};

// Cucine
export const cucine = {
  list: () => apiFetch('/cucine'),
  create: (data) => apiFetch('/cucine', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiFetch(`/cucine/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiFetch(`/cucine/${id}`, { method: 'DELETE' }),
};

// Menu
export const menu = {
  list: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return apiFetch(`/menu?${searchParams}`);
  },
  create: (data) => apiFetch('/menu', { method: 'POST', body: JSON.stringify(data) }),
};

// Mezzi
export const mezzi = {
  list: () => apiFetch('/mezzi'),
  create: (data) => apiFetch('/mezzi', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiFetch(`/mezzi/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiFetch(`/mezzi/${id}`, { method: 'DELETE' }),
};

// Attrezzature
export const attrezzature = {
  list: () => apiFetch('/attrezzature'),
  create: (data) => apiFetch('/attrezzature', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiFetch(`/attrezzature/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiFetch(`/attrezzature/${id}`, { method: 'DELETE' }),
};

// Turni
export const turni = {
  list: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return apiFetch(`/turni?${searchParams}`);
  },
  create: (data) => apiFetch('/turni', { method: 'POST', body: JSON.stringify(data) }),
  assegna: (turnoId, personaId) => apiFetch(`/turni/${turnoId}/assegna?persona_id=${personaId}`, { method: 'PATCH' }),
  delete: (id) => apiFetch(`/turni/${id}`, { method: 'DELETE' }),
};

// Stats
export const stats = {
  dashboard: () => apiFetch('/stats/dashboard'),
  pastiGiornalieri: (giorni = 7) => apiFetch(`/stats/pasti-giornalieri?giorni=${giorni}`),
  accessiOrari: () => apiFetch('/stats/accessi-orari'),
};

// Users
export const users = {
  list: () => apiFetch('/users'),
  updateRole: (userId, role) => apiFetch(`/users/${userId}/role?role=${role}`, { method: 'PATCH' }),
};

// Audit Log
export const auditLog = {
  list: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return apiFetch(`/audit-log?${searchParams}`);
  },
};

// GDPR
export const gdpr = {
  exportPersona: (personaId) => apiFetch(`/gdpr/export/${personaId}`),
};

export { API, BACKEND_URL };

// Tende (Alloggi)
export const tende = {
  list: () => apiFetch('/tende'),
  listDisponibili: () => apiFetch('/tende/disponibili'),
  create: (data) => apiFetch('/tende', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiFetch(`/tende/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiFetch(`/tende/${id}`, { method: 'DELETE' }),
};

// Alloggio persona
export const alloggio = {
  assegna: (personaId, tendaId, necessitaAlloggio = true) => 
    apiFetch(`/persone/${personaId}/alloggio?tenda_id=${tendaId || ''}&necessita_alloggio=${necessitaAlloggio}`, { method: 'PATCH' }),
};

// ==================== REPORTS ====================
export const reports = {
  inventario: () => apiFetch('/reports/inventario'),
  volontari: () => apiFetch('/reports/volontari'),
};
